import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Button from "../customComponents/Button";
import Paper from "../widgets/Paper";
import InputText from "../customComponents/InputText";
import TextArea from "../customComponents/TextArea";
import DatePicker from "../customComponents/DatePicker";
import Select from "../customComponents/Select";
import useFetchOptions from "./hooks/useFetchOptions";
import { post, put } from "../utils/api";
import { ToastContainer, toast } from "react-toastify";
import Dropdown from "../customComponents/Dropdown";

const AddEnquiry = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    enquiry = {},
    institution = {},
    editenquiry,
    institutionId,
    packageData,
    itineraryId
  } = location.state || {};
  // console.log('enquiry',itineraryId)

  const user = React.useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user"));
    } catch {
      return null;
    }
  }, []);

  // --- option hooks called once each
  const enquiryStatusOptions = useFetchOptions("enquirystatus");
  const packageTypeOptions = useFetchOptions("packagetype");
  const hotelTypeOptions = useFetchOptions("hoteltype");
  const accommodationOptions = useFetchOptions("accommodationtype");
  const onwardOptions = useFetchOptions("onwardtrn");
  const returnOptions = useFetchOptions("returntrn");
  const localOptions = useFetchOptions("localtrn");

  // --- local UI state
  const [selectedValue, setSelectedValue] = useState(
    enquiry?.name || institution?.name || institution?.label || null
  );
  const [packageOptions, setPackageOptions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [travelDate, setTravelDate] = useState(() => new Date());

  const initialEnquiryStatusId = { value: 1, label: "Opened" };
  const [showAdditionalDatePicker, setShowAdditionalDatePicker] = useState(
    initialEnquiryStatusId?.value === 1
  );

  const [formData, setFormData] = useState(() => ({
    enquiryname: "",
    enquirystatusid: initialEnquiryStatusId,
    packagetypeid: null,
    selectedAccommodation: null,
    selectedHotel: null,
    selectedays: null,
    selectedpax: "",
    selectedreturn: null,
    selectedOnward: null,
    selectednights: null,
    enquirysource: "",
    destination: "",
    referenceId: null,
    referenceComment: "",
    comments: "",
    prices: "",
    selectedLocal: null,
  }));

  // keep a ref of previous serialized inputs to avoid repeated setState
  const prevInitRef = useRef("");

  // --- stable callbacks
  const handleDateChange = useCallback((date, type) => {
    // normalize to local date (removes timezone offset)
    const adjustedDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    if (type === "dateoftravel") setSelectedDate(adjustedDate);
    else setTravelDate(adjustedDate);
  }, []);

  const handleNavigateBack = useCallback(() => navigate(-1), [navigate]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    if (["selectedpax", "selectedays", "selectednights"].includes(name)) {
      if (!/^\d{0,3}$/.test(value)) return;
    } else if (name === "referenceId") {
      if (!(/^\d*$/.test(value) && value.length <= 10)) return;
    } else if (["enquiryname", "comments", "enquirysource", "destination"].includes(name)) {
      if (value.length > 100) return;
    } else if (name === "referenceComment") {
      if (value.length > 300) return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSelectChange = useCallback((name, option) => {
    setFormData((prev) => ({ ...prev, [name]: option || null }));
    if (name === "enquirystatusid") setShowAdditionalDatePicker(option?.value === 1);
  }, []);

  // --- initialize formData from enquiry or packageData only when inputs change meaningfully
  useEffect(() => {
    const seed = { enquiry, packageData };
    const seedStr = JSON.stringify(seed);
    if (prevInitRef.current === seedStr) {
      return; // already applied
    }

    // prepare newForm object
    const newForm = { ...formData }; // start from current to avoid losing user edits when partial
    if (Object.keys(enquiry || {}).length > 0) {
      const statusId = enquiry.enquirystatusid || 1;
      newForm.enquiryname = enquiry.enquiryname || "";
      newForm.enquirystatusid = { value: statusId, label: statusId === 1 ? "Opened" : statusId === 2 ? "Confirmed" : "" };
      newForm.packagetypeid = enquiry.packageTypeId ? { value: enquiry.packageTypeId, label: enquiry.packageType } : null;
      newForm.selectedAccommodation = enquiry.accommodationtypeid ? { value: enquiry.accommodationtypeid, label: enquiry.accommodationtype } : null;
      newForm.selectedHotel = enquiry.hoteltypeid ? { value: enquiry.hoteltypeid, label: enquiry.hoteltype } : null;
      newForm.selectedays = enquiry.noofdays || null;
      newForm.selectedpax = enquiry.noofpax?.toString() || "";
      newForm.selectedreturn = enquiry.returnId ? { value: enquiry.returnId, label: enquiry.return } : null;
      newForm.selectedOnward = enquiry.onwardId ? { value: enquiry.onwardId, label: enquiry.onward } : null;
      newForm.selectednights = enquiry.noofnight || null;
      newForm.enquirysource = enquiry.enquirysource || "";
      newForm.destination = enquiry.destination || "";
      newForm.referenceId = enquiry.referenceid ? { value: enquiry.referenceid, label: enquiry.referencecomment || "" } : null;
      newForm.referenceComment = enquiry.referencecomment || "";
      newForm.comments = enquiry.enquirycomments || "";
      newForm.prices = enquiry.prices || "";
      newForm.selectedLocal = enquiry.localtrnid ? { value: enquiry.localtrnid, label: enquiry.localtrn } : null;

      if (enquiry.dateoftravel) {
        setSelectedDate(new Date(enquiry.dateoftravel));
      }
      if (enquiry.nextfollowupdate) {
        setTravelDate(new Date(enquiry.nextfollowupdate));
      }
      setShowAdditionalDatePicker(statusId === 1);
    } else if (packageData) {
      const extractHotelType = (fullName) => (fullName ? fullName.split("-")[1]?.trim() || fullName : "");
      const extractTransportType = (fullName) => (fullName ? fullName.split("-")[0]?.trim() : "");

      newForm.enquiryname = "";
      newForm.enquirystatusid = { value: 1, label: "Opened" };
      newForm.packagetypeid = packageData.packagetypeid ? { value: packageData.packagetypeid, label: packageData.packagetype } : null;
      newForm.selectedHotel = packageData.hotelType ? { value: packageData.hotelType, label: packageData.hotelType } : null;
      newForm.selectedAccommodation = packageData.packagewise_plan?.[0]?.mealPlan ? { value: packageData.packagewise_plan[0].mealPlan.label, label: packageData.packagewise_plan[0].mealPlan.label } : null;
      newForm.selectedOnward = packageData.onwardtrnid ? { value: packageData.onwardtrnid, label: extractTransportType(packageData.onward) } : null;
      newForm.selectedreturn = packageData.returnid ? { value: packageData.returnid, label: extractTransportType(packageData.return) } : null;
      newForm.selectedLocal = packageData.transferid ? { value: packageData.transferid, label: extractHotelType(packageData.transfer) } : null;
      newForm.selectedays = packageData.noofdays || "";
      newForm.selectedpax = packageData.noofpax?.toString() || "";
      newForm.selectednights = packageData.noofnights || "";
      newForm.enquirysource = packageData.source || "";
      newForm.destination = packageData.destination || "";
      newForm.comments = packageData.comments || "";
      newForm.prices = packageData.total_amount?.toString() || "";
      newForm.referenceId = packageData.packagedetailsid ? { value: packageData.packagedetailsid, label: packageData.packagename } : null;

      if (packageData.dateoftravel) {
        setSelectedDate(new Date(packageData.dateoftravel));
      }
    }

    // commit new form data
    setFormData(newForm);
    prevInitRef.current = seedStr;
  }, [enquiry, packageData]); // we intentionally only depend on those two

  // --- fetch packages once
  const fetchPackages = useCallback(async () => {
    try {
      const result = await post("/getOptionList", { type: "packages" });
      setPackageOptions(result.data || []);
    } catch (err) {
      console.error("Error fetching packages:", err);
    }
  }, []);

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  // --- memoize fieldConfig to avoid recreating and causing Select to rerender
  const fieldConfig = useMemo(() => {
    return [
      { label: "Enquiry Name", name: "enquiryname", component: InputText },
      {
        label: "Enquiry Status",
        name: "enquirystatusid",
        component: Select,
        fetchOptions: enquiryStatusOptions,
        disabled: !editenquiry,
      },
      {
        label: "Package Type",
        name: "packagetypeid",
        component: Select,
        fetchOptions: packageTypeOptions,
      },
      { label: "Source", name: "enquirysource", component: InputText },
      { label: "Destination", name: "destination", component: InputText },
      {
        label: "Destination Reference Comment",
        name: "referenceComment",
        component: TextArea,
      },
      {
        label: "Package",
        name: "referenceId",
        component: Select,
        options: packageOptions,
        skipAsterisk: true,
        disabled: !!location.state?.packageData,
        value: formData.referenceId,
      },
      {
        label: "Hotel Type",
        name: "selectedHotel",
        component: Select,
        fetchOptions: hotelTypeOptions,
      },
      {
        label: "Accommodation Type",
        name: "selectedAccommodation",
        component: Select,
        fetchOptions: accommodationOptions,
      },
      {
        label: "Onward Transportation",
        name: "selectedOnward",
        component: Select,
        fetchOptions: onwardOptions,
      },
      {
        label: "Return Transportation",
        name: "selectedreturn",
        component: Select,
        fetchOptions: returnOptions,
      },
      {
        label: "Local Transportation",
        name: "selectedLocal",
        component: Select,
        fetchOptions: localOptions,
        skipAsterisk: true,
      },
      { label: "No. of Pax", name: "selectedpax", component: InputText },
      { label: "No. of Days", name: "selectedays", component: InputText },
      { label: "No. of Nights", name: "selectednights", component: InputText },
      { label: "Comments", name: "comments", component: InputText },
      {
        label: "Prices",
        name: "prices",
        component: InputText,
        condition: formData.enquirystatusid?.value === 2,
      },
    ];
  }, [
    enquiryStatusOptions,
    packageTypeOptions,
    hotelTypeOptions,
    accommodationOptions,
    onwardOptions,
    returnOptions,
    localOptions,
    packageOptions,
    editenquiry,
    formData.enquirystatusid?.value,
    location.state?.packageData,
  ]);

  const handleSelect = useCallback((value) => {
    setSelectedValue(value?.value);
    setSelectedId(value?.institutionid);
  }, []);

  // --- save handler (memoized)
  const handleSave = useCallback(async () => {
    // basic validation
    if (
      !formData.enquiryname ||
      !formData.enquirystatusid?.value ||
      !formData.packagetypeid?.value ||
      !formData.selectedAccommodation?.value ||
      !formData.selectedays ||
      !formData.selectedpax ||
      !formData.selectednights ||
      !formData.enquirysource ||
      !formData.destination ||
      !formData.comments ||
      (formData.enquirystatusid?.value === 2 && !formData.prices) ||
      !selectedDate ||
      !travelDate
    ) {
      toast.error("Please fill out all required fields");
      return;
    }

    const payload = {
      institutionid: institution?.institutionid ?? institutionId ?? selectedId ?? null,
      enquiryname: formData.enquiryname,
      enquirystatusid: formData.enquirystatusid?.value ?? null,
      packagetypeid: (formData.packagetypeid?.value ?? formData.packagetypeid?.id) ?? null,
      accommodationtypeid: formData.selectedAccommodation?.value ?? null,
      hoteltypeid: formData.selectedHotel?.value ?? null,
      onwardtrnid: formData.selectedOnward?.value ?? null,
      returntrnid: formData.selectedreturn?.value ?? null,
      localtrnid: formData.selectedLocal?.value ?? null,
      noofpax: formData.selectedpax ?? null,
      noofdays: formData.selectedays ?? null,
      noofnight: formData.selectednights ?? null,
      enquirysource: formData.enquirysource ?? "",
      destination: formData.destination ?? "",
      referenceid: formData.referenceId?.value ?? "",
      referencecomment: formData.referenceComment ?? "",
      enquirycomments: formData.comments ?? "",
      prices: formData.prices ?? "",
      dateoftravel: selectedDate.toISOString().split("T")[0],
      nextfollowupdate: travelDate.toISOString().split("T")[0],
      type: editenquiry ? "" : "insert",
      ...(enquiry?.enquiryid && { enquiryid: enquiry.enquiryid }),
      userid: user?.userid ?? null,
      user: user?.username ?? "N/A",
      packagedetailsid: packageData?.packagedetailsid ?? null,
    };

    try {
      const response = await post("/addInstitutionEnquiry", payload);
      console.log('response',response)
      if (response.status) {
        const enquiry=response.data.enquiryid
        toast.success("Enquiry saved successfully");
        if(itineraryId?.length){
          await put(`/update-enquiry/${itineraryId}`,{enquiryid:enquiry})
        }
        // if(enquiryUpdate.status)
        const navigateTo = location.state?.packageData ? "/all-itinerary" : -1;
        setTimeout(() => {
          if (typeof navigateTo === "string") navigate(navigateTo);
          else navigate(navigateTo, { state: { institution } });
        }, 800);
      } else {
        toast.error("Error: Non-successful response");
      }
    } catch (err) {
      console.error("Save error:", err);
      toast.error("Error saving enquiry");
    }
  }, [formData, selectedDate, travelDate, selectedId, editenquiry, enquiry, institution, institutionId, packageData, user, navigate, location.state]);

  // useEffect to fetch packages on mount already handled above with fetchPackages

  return (
    <div>
      <div className="block md:flex justify-between ">
        <div>
          <h2 className="font-medium text-2xl">Add Enquiry</h2>
          <p className="text-[12px]">
            <span className=" font-bold">
              {institution?.name || selectedValue || "Institutions"}
            </span>{" "}
            /<span className="font-regular"> Add Enquiry</span>
          </p>
        </div>

        <div className="flex mt-4 justify-end gap-4">
          <Button type="primary" label="Save" onClick={handleSave} />
          <Button type="secondary" label="Cancel" onClick={handleNavigateBack} />
        </div>
      </div>

      <Paper className="my-6 py-8 border">
        <div className=" block md:flex  justify-between">
          <div>
            <h1 className="text-[20px] font-bold mb-6">
              Enquiry{" "}
              <span className="bg-[#797979] text-white pt-1 pb-1 px-[12px] rounded-[8px] text-[14px] font-semibold">
                {formData.enquirystatusid?.label}
              </span>
            </h1>
          </div>
          <div className="pb-4">
            <Dropdown
              apiUrl="/getOptionList"
              payload={{ type: "institution" }}
              placeholder="Select Institutions"
              onSelect={handleSelect}
              value={selectedValue || institution?.institutionid}
              disabled={editenquiry || (institution?.institutionid ? true : false)}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {fieldConfig.map((field, index) => {
            if (field.condition === false) return null;
            const { options, loading } = field.fetchOptions || {};
            return (
              <div key={index}>
                <label className="text-[16px] font-semibold mb-2">
                  {field.label}
                  {!field.skipAsterisk && field.name !== "referenceComment" && (
                    <span className="text-red-500">*</span>
                  )}
                </label>
                {field.component === InputText && (
                  <InputText
                    placeholder={field.label}
                    value={formData[field.name] ?? ""}
                    onChange={handleChange}
                    name={field.name}
                  />
                )}
                {field.component === Select && (
                  <Select
                    options={options || field.options}
                    value={formData[field.name]}
                    onChange={(option) => handleSelectChange(field.name, option)}
                    placeholder={`Select ${field.label}`}
                    isLoading={loading}
                    disabled={field.disabled}
                  />
                )}
                {field.component === TextArea && (
                  <TextArea
                    placeholder={field.label}
                    value={formData[field.name] ?? ""}
                    onChange={handleChange}
                    name={field.name}
                  />
                )}
              </div>
            );
          })}
        </div>

        <div className="grid grid-flow-row-dense grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-5">
          <div>
            <label className="text-[16px] font-semibold mb-2">
              Date of Travel <span className="text-red-500">*</span>
            </label>
            <DatePicker
              selectedDate={selectedDate}
              onDateChange={(date) => handleDateChange(date, "dateoftravel")}
            />
          </div>
          {showAdditionalDatePicker && (
            <div>
              <label className="text-[16px] font-semibold mb-2">
                Next Follow Up Date
                <span className="text-red-500">*</span>
              </label>
              <DatePicker
                selectedDate={travelDate}
                onDateChange={(date) => handleDateChange(date, "nextfollowupdate")}
              />
            </div>
          )}
        </div>
      </Paper>
      <ToastContainer />
    </div>
  );
};

export default AddEnquiry;
